
/**
 * ValuationResult Component
 * Created: 2025-05-24 - Added to ensure ValuationForm renders properly
 * Updated: 2025-05-25 - Modified to only show reserve price to sellers
 * Updated: 2025-05-26 - Added onClick handler for List My Car button and loading state feedback
 * Updated: 2025-09-07 - Added manual valuation redirection for NO_DATA scenarios
 * Updated: 2025-09-07 - Fixed NO_DATA scenario detection by adding proper condition checks for result.noData, invalid reservePrice values, and comprehensive logging
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LockIcon, Loader2 } from "lucide-react";

interface ValuationResultProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: any;
  onReset: () => void;
  onContinue?: () => void; // Add onContinue prop
}

export const ValuationResult: React.FC<ValuationResultProps> = ({
  open,
  onOpenChange,
  result,
  onReset,
  onContinue
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  
  // Handle continue button click with processing state
  const handleContinue = () => {
    if (onContinue) {
      setIsProcessing(true);
      
      // Log the click action for debugging
      console.log("ValuationResult: List My Car button clicked", {
        hasHandler: !!onContinue,
        resultData: {
          make: result?.make,
          model: result?.model,
          vin: result?.vin,
          reservePrice: result?.reservePrice
        },
        timestamp: new Date().toISOString()
      });
      
      // Call the onContinue function from props
      onContinue();
      
      // Reset processing state after a timeout in case navigation fails
      setTimeout(() => {
        setIsProcessing(false);
      }, 5000);
    } else {
      console.error("ValuationResult: No continue handler provided");
    }
  };

  // Handle manual valuation scenarios with user choice
  const handleManualValuation = () => {
    // Store vehicle data for manual valuation form
    if (result.vin) {
      localStorage.setItem('tempVIN', result.vin);
    }
    if (result.mileage) {
      localStorage.setItem('tempMileage', result.mileage.toString());
    }
    if (result.transmission || result.gearbox) {
      localStorage.setItem('tempGearbox', result.transmission || result.gearbox);
    }

    // Close current dialog
    onOpenChange(false);
    
    // Navigate to manual valuation
    navigate('/manual-valuation');
    toast.info("Przekierowanie do ręcznej wyceny", {
      description: "Brak automatycznych danych wyceny dla tego pojazdu.",
    });
  };

  // Add comprehensive logging for debugging
  useEffect(() => {
    if (result) {
      console.log("ValuationResult: Complete result object:", {
        result,
        hasNoData: !!result.noData,
        hasCode: !!result.code,
        code: result.code,
        hasError: !!result.error,
        error: result.error,
        hasReservePrice: !!result.reservePrice,
        reservePrice: result.reservePrice,
        reservePriceType: typeof result.reservePrice,
        requiresManualValuation: result.requiresManualValuation
      });
    }
  }, [result]);

  // Check for NO_DATA scenarios - updated conditions based on actual API response
  const isNoDataScenario = result && (
    result.noData === true ||  // Added: Check for noData flag set by useValuationForm.ts
    result.requiresManualValuation || 
    result.code === 'NO_DATA_AVAILABLE' || 
    result.code === 'NO_PRICE_DATA' ||
    result.reservePrice === null ||  // Added: Check for null reserve price
    result.reservePrice === undefined ||  // Added: Check for undefined reserve price  
    result.reservePrice === 'Not available' ||  // Added: Check for "Not available" string
    (typeof result.reservePrice === 'number' && (isNaN(result.reservePrice) || result.reservePrice <= 0)) ||  // Added: Check for invalid numbers
    (result.error && (result.error.includes('No data') || result.error.includes('No price')))
  );

  console.log("ValuationResult: NO_DATA scenario check:", {
    isNoDataScenario,
    hasResult: !!result,
    conditions: result ? {
      noData: result.noData === true,
      requiresManualValuation: result.requiresManualValuation,
      codeNoData: result.code === 'NO_DATA_AVAILABLE',
      codeNoPrice: result.code === 'NO_PRICE_DATA', 
      reservePriceNull: result.reservePrice === null,
      reservePriceUndefined: result.reservePrice === undefined,
      reservePriceNotAvailable: result.reservePrice === 'Not available',
      reservePriceInvalid: typeof result.reservePrice === 'number' && (isNaN(result.reservePrice) || result.reservePrice <= 0),
      errorContainsNoData: result.error && (result.error.includes('No data') || result.error.includes('No price'))
    } : {}
  });

  // Show manual valuation dialog for NO_DATA scenarios
  if (isNoDataScenario) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wymagana manualna wycena</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Brak dostępnych danych do automatycznej wyceny tego samochodu.
            </p>
            <p className="text-sm text-gray-600">
              Możesz kontynuować uzupełnianie profilu swojego samochodu, a nasz zespół go oceni i przygotuje wycenę na podstawie podanych informacji.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onReset}>Wróć</Button>
            <Button onClick={handleManualValuation}>
              Przejdź do profilu
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Handle successful valuation results - showing only reserve price
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Wycena twojego samochodu</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {result ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center border-b pb-2">
                <span className="font-medium">Samochód:</span>
                <span>{result.make} {result.model} {result.year}</span>
              </div>
              
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    Twoja wstępna wycena
                    <LockIcon size={14} className="text-gray-500" />
                  </h3>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-2xl font-bold text-primary">
                    {typeof result.reservePrice === 'number' 
                      ? `${result.reservePrice.toLocaleString()} PLN`
                      : 'Not available'}
                  </p>
                  
                  <div className="mt-3 p-3 bg-[#DC143C]/10 border border-[#DC143C]/20 rounded-md">
                    <p className="text-xs text-[#454545]">
                      To orientacyjna wycena Twojego auta. Nasza sieć zweryfikowanych komisów może złożyć ofertę wyższą lub niższą — zależnie od faktycznego stanu pojazdu
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p>No valuation data available</p>
          )}
        </div>
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onReset} disabled={isProcessing}>
            Nowa wycena
          </Button>
          <Button 
            className="bg-primary hover:bg-primary/90 relative" 
            onClick={handleContinue}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                Processing...
              </span>
            ) : (
              "Sprzedaj samochód"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
